import type { DemoCustomer, DemoOrder, HistoryEntry, PanelSectionContent } from "@/pages/painel/types";

export const PANEL_MENU = [
  { id: "visao-geral", label: "Visão geral", icon: "ri-dashboard-line" },
  { id: "ficha", label: "Ficha do site", icon: "ri-file-list-3-line" },
  { id: "editar-site", label: "Editar site", icon: "ri-edit-2-line" },
  { id: "categorias", label: "Categorias", icon: "ri-apps-2-line" },
  { id: "catalogo", label: "Catálogo", icon: "ri-t-shirt-line" },
  { id: "clientes", label: "Clientes", icon: "ri-user-heart-line" },
  { id: "pedidos", label: "Pedidos", icon: "ri-shopping-bag-3-line" },
  { id: "entregas", label: "Entregas", icon: "ri-truck-line" },
  { id: "whatsapp", label: "WhatsApp", icon: "ri-whatsapp-line" },
  { id: "configuracoes", label: "Configurações", icon: "ri-settings-3-line" },
  { id: "seo", label: "SEO e compartilhamento", icon: "ri-global-line" },
  { id: "historico", label: "Histórico", icon: "ri-history-line" },
] as const;

/** Seções da página inicial, na ordem exata de cima para baixo. */
export const siteSections: PanelSectionContent[] = [
  {
    id: "topbar",
    name: "Barra superior",
    position: 1,
    description: "Faixa de mensagens rotativas no topo do site.",
    texts: [
      { label: "Mensagem 1", value: "Atendimento personalizado pelo WhatsApp" },
      { label: "Mensagem 2", value: "Escolha suas peças e finalize com facilidade" },
      { label: "Mensagem 3", value: "Entrega e retirada combinada no atendimento" },
      { label: "Mensagem 4", value: "Moda feminina para todos os seus momentos" },
    ],
    images: [],
    colors: [{ label: "Fundo", value: "primary-700" }],
    links: [],
    desktop: "Mensagens em rolagem contínua e discreta.",
    mobile: "Mesmas mensagens, com fonte reduzida.",
    lastChange: "19/09/2026",
    canRestore: true,
  },
  {
    id: "header",
    name: "Cabeçalho e menu",
    position: 2,
    description: "Logo, navegação por categorias e atalhos de busca, favoritos e sacola.",
    texts: [
      { label: "Marca", value: "Amora Belle" },
      { label: "Assinatura curta", value: "moda feminina" },
    ],
    images: [],
    colors: [
      { label: "Marca", value: "primary-700" },
      { label: "Fundo ao rolar", value: "background-50" },
    ],
    links: [
      { label: "Novidades", href: "/novidades" },
      { label: "Blusas", href: "/categoria/blusas" },
      { label: "Calças", href: "/categoria/calcas" },
      { label: "Vestidos", href: "/categoria/vestidos" },
      { label: "Macacões", href: "/categoria/macacoes" },
      { label: "Bermudas", href: "/categoria/bermudas" },
      { label: "Conjuntos", href: "/categoria/conjuntos" },
      { label: "Saias", href: "/categoria/saias" },
    ],
    desktop: "Navegação completa em linha, alinhada ao logo.",
    mobile: "Menu lateral com rolagem interna e botão de fechar sempre visível.",
    lastChange: "19/09/2026",
    canRestore: true,
  },
  {
    id: "hero",
    name: "Hero principal",
    position: 3,
    description: "Abertura da página inicial com imagem de fundo, título e chamadas.",
    texts: [
      { label: "Selo", value: "Nova coleção Amora Belle" },
      { label: "Título", value: "Moda para viver cada versão de você" },
      {
        label: "Subtítulo",
        value:
          "Peças que acompanham sua rotina, valorizam seu estilo e fazem você se sentir bem em todos os momentos.",
      },
      { label: "Botão 1", value: "Ver novidades" },
      { label: "Botão 2", value: "Conhecer as coleções" },
    ],
    images: [
      {
        label: "Imagem de fundo",
        url: "https://readdy.ai/api/search-image?query=Editorial%20fashion%20photography%20of%20a%20confident%20Brazilian%20woman%20with%20natural%20curly%20hair%20wearing%20an%20elegant%20contemporary%20feminine%20outfit%20in%20deep%20plum%20and%20cream%20tones%2C%20standing%20in%20a%20warm%20sunlit%20urban%20Brazilian%20setting%20with%20soft%20neutral%20walls%2C%20natural%20daylight%2C%20realistic%20skin%20texture%2C%20warm%20welcoming%20mood&width=1600&height=1000&seq=amora-hero-main-01&orientation=landscape",
      },
    ],
    colors: [{ label: "Botão principal", value: "primary-500" }],
    links: [
      { label: "Ver novidades", href: "#novidades" },
      { label: "Conhecer as coleções", href: "#looks" },
    ],
    desktop: "Altura ampla com imagem full-bleed e texto à esquerda.",
    mobile: "Altura reduzida, mantendo título e botões legíveis.",
    lastChange: "19/09/2026",
    canRestore: true,
  },
  {
    id: "beneficios",
    name: "Benefícios",
    position: 4,
    description: "Quatro destaques de atendimento, compra, entrega e troca.",
    texts: [
      { label: "Destaque 1", value: "Atendimento com carinho" },
      { label: "Destaque 2", value: "Compra facilitada pelo WhatsApp" },
      { label: "Destaque 3", value: "Entrega e retirada combinadas" },
      { label: "Destaque 4", value: "Troca orientada pela equipe" },
    ],
    images: [],
    colors: [{ label: "Ícones", value: "primary-600" }],
    links: [],
    desktop: "Quatro colunas alinhadas.",
    mobile: "Uma coluna, com respiro adequado.",
    lastChange: "19/09/2026",
    canRestore: true,
  },
  {
    id: "categorias",
    name: "Categorias",
    position: 5,
    description: "Vitrine das oito categorias com imagem e ligação para cada página.",
    texts: [{ label: "Título da seção", value: "Encontre seu estilo por categoria" }],
    images: [
      {
        label: "Blusas",
        url: "https://readdy.ai/api/search-image?query=Editorial%20fashion%20photography%20of%20a%20Brazilian%20woman%20with%20curly%20hair%20wearing%20an%20elegant%20cream%20blouse%20with%20soft%20draping%2C%20clearly%20a%20feminine%20blouse%20as%20the%20upper%20garment%2C%20standing%20in%20a%20bright%20neutral%20studio%20with%20warm%20beige%20background%2C%20natural%20daylight%2C%20realistic%20photographic%20skin%20texture%2C%20full%20body%2C%20no%20text%2C%20no%20logos&width=700&height=900&seq=amora-cat-blusas-01&orientation=portrait",
      },
      {
        label: "Bermudas",
        url: "https://readdy.ai/api/search-image?query=Editorial%20fashion%20photography%20of%20a%20Brazilian%20woman%20standing%20full%20body%20wearing%20high%20waist%20structured%20bermuda%20shorts%20whose%20hem%20clearly%20ends%20well%20above%20the%20knee%20with%20bare%20legs%20and%20knees%20fully%20visible%2C%20one%20short%20lower%20garment%20clearly%20shorter%20than%20the%20knee%2C%20not%20trousers%20not%20long%20pants%20not%20cropped%20long%20pants%2C%20in%20soft%20sand%20tone%20with%20a%20flowy%20blouse%2C%20standing%20on%20a%20warm%20sunlit%20city%20sidewalk%20with%20soft%20afternoon%20light%2C%20realistic%20photographic%20skin%20texture%2C%20full%20body%20including%20legs%2C%20no%20text%2C%20no%20logos&width=700&height=900&seq=amora-cat-bermudas-05&orientation=portrait",
      },
    ],
    colors: [{ label: "Cartões", value: "background-100" }],
    links: [
      { label: "Blusas", href: "/categoria/blusas" },
      { label: "Calças", href: "/categoria/calcas" },
      { label: "Vestidos", href: "/categoria/vestidos" },
      { label: "Macacões", href: "/categoria/macacoes" },
      { label: "Bermudas", href: "/categoria/bermudas" },
      { label: "Conjuntos", href: "/categoria/conjuntos" },
      { label: "Saias", href: "/categoria/saias" },
      { label: "Novidades", href: "/novidades" },
    ],
    desktop: "Grade de cartões retrato com oito categorias.",
    mobile: "Grade de duas colunas.",
    lastChange: "19/09/2026",
    canRestore: true,
  },
  {
    id: "selecao",
    name: "Seleção Amora Belle",
    position: 6,
    description: "Destaque de peças selecionadas do catálogo.",
    texts: [{ label: "Título da seção", value: "Seleção Amora Belle" }],
    images: [],
    colors: [{ label: "Cartões", value: "background-50" }],
    links: [{ label: "Ver novidades", href: "/novidades" }],
    desktop: "Grade de produtos em destaque.",
    mobile: "Grade adaptada com rolagem confortável.",
    lastChange: "19/09/2026",
    canRestore: true,
  },
  {
    id: "editorial",
    name: "Banner editorial",
    position: 7,
    description: "Faixa editorial com imagem de fundo e chamada para a coleção.",
    texts: [
      { label: "Selo", value: "Editorial Amora Belle" },
      { label: "Título", value: "Elegância que acompanha a vida real" },
      {
        label: "Subtítulo",
        value:
          "Do trabalho ao encontro especial, descubra peças pensadas para deixar sua rotina mais bonita, confortável e cheia de personalidade.",
      },
      { label: "Botão", value: "Descobrir a coleção" },
    ],
    images: [
      {
        label: "Imagem de fundo",
        url: "https://readdy.ai/api/search-image?query=Editorial%20fashion%20photography%20of%20an%20elegant%20Brazilian%20woman%20in%20a%20refined%20feminine%20look%20with%20warm%20plum%20and%20beige%20tones%2C%20standing%20in%20a%20sunlit%20Brazilian%20urban%20interior%20with%20soft%20neutral%20walls%2C%20natural%20daylight%2C%20realistic%20skin%20texture%2C%20welcoming%20mood&width=1600&height=900&seq=amora-banner-editorial-01&orientation=landscape",
      },
    ],
    colors: [{ label: "Selo", value: "secondary-300" }],
    links: [{ label: "Descobrir a coleção", href: "#selecao" }],
    desktop: "Imagem full-bleed com texto à esquerda.",
    mobile: "Texto sobreposto com contraste reforçado.",
    lastChange: "19/09/2026",
    canRestore: true,
  },
  {
    id: "looks",
    name: "Looks para cada momento",
    position: 8,
    description: "Quatro momentos com pares de imagens de composição.",
    texts: [
      { label: "Momento 1", value: "Para trabalhar" },
      { label: "Momento 2", value: "Para sair" },
      { label: "Momento 3", value: "Para celebrar" },
      { label: "Momento 4", value: "Para aproveitar o fim de semana" },
    ],
    images: [],
    colors: [{ label: "Cartões", value: "background-100" }],
    links: [],
    desktop: "Quatro blocos com pares de imagens.",
    mobile: "Blocos empilhados com imagens lado a lado.",
    lastChange: "19/09/2026",
    canRestore: true,
  },
  {
    id: "novidades",
    name: "Novidades",
    position: 9,
    description: "Peças recém-chegadas, ligadas ao mesmo catálogo único.",
    texts: [{ label: "Título da seção", value: "Acabaram de chegar" }],
    images: [],
    colors: [{ label: "Selo Novo", value: "secondary-500" }],
    links: [{ label: "Ver novidades", href: "/novidades" }],
    desktop: "Grade de produtos marcados como novos.",
    mobile: "Grade com duas colunas.",
    lastChange: "19/09/2026",
    canRestore: true,
  },
  {
    id: "atendimento",
    name: "Atendimento",
    position: 10,
    description: "Seção de atendimento humanizado com imagem e lista de apoio.",
    texts: [
      { label: "Título", value: "Precisa de ajuda para escolher?" },
      {
        label: "Texto",
        value:
          "Conte para a gente o que você procura. Ajudamos você a encontrar tamanhos, combinações e peças que fazem sentido para o seu estilo e para a sua rotina.",
      },
      { label: "Botão", value: "Falar com a gente" },
    ],
    images: [
      {
        label: "Imagem",
        url: "https://readdy.ai/api/search-image?query=Warm%20lifestyle%20photo%20of%20a%20Brazilian%20fashion%20consultant%20helping%20a%20client%20choose%20clothes%20in%20a%20cozy%20boutique%20with%20neutral%20beige%20interiors%2C%20natural%20daylight%2C%20realistic%20skin%20texture%2C%20friendly%20genuine%20expressions%2C%20no%20text%2C%20no%20logos&width=1000&height=800&seq=amora-atendimento-consultora-01&orientation=landscape",
      },
    ],
    colors: [{ label: "Selo", value: "accent-700" }],
    links: [{ label: "Falar com a gente", href: "https://wa.me/message/6X77HRWZGKPQH1" }],
    desktop: "Imagem e texto lado a lado.",
    mobile: "Imagem acima do texto.",
    lastChange: "19/09/2026",
    canRestore: true,
  },
  {
    id: "historia",
    name: "História da marca",
    position: 11,
    description: "Nossa essência, com imagem e três pilares.",
    texts: [
      { label: "Selo", value: "Nossa essência" },
      { label: "Título", value: "Moda feita para mulheres reais" },
      { label: "Pilar 1", value: "Diversidade — de corpos e estilos" },
      { label: "Pilar 2", value: "Cuidado — em cada detalhe" },
      { label: "Pilar 3", value: "Proximidade — no atendimento" },
    ],
    images: [
      {
        label: "Imagem",
        url: "https://readdy.ai/api/search-image?query=Editorial%20behind%20the%20scenes%20photo%20of%20a%20clothing%20rack%20with%20feminine%20garments%20in%20warm%20plum%20cream%20and%20beige%20tones%2C%20a%20hand%20organizing%20soft%20fabrics%20in%20a%20minimal%20warm%20atelier%20with%20natural%20daylight%2C%20no%20text%2C%20no%20logos&width=900&height=1100&seq=amora-historia-bastidores-01&orientation=portrait",
      },
    ],
    colors: [{ label: "Fundo", value: "background-100" }],
    links: [],
    desktop: "Texto e imagem em duas colunas.",
    mobile: "Imagem acima do texto.",
    lastChange: "19/09/2026",
    canRestore: true,
  },
  {
    id: "inspiracoes",
    name: "Inspirações",
    position: 12,
    description: "Galeria com oito imagens em proporções quadrada e vertical.",
    texts: [{ label: "Título da seção", value: "Inspirações Amora Belle" }],
    images: [],
    colors: [{ label: "Fundo", value: "background-50" }],
    links: [],
    desktop: "Mosaico com imagens quadradas e verticais.",
    mobile: "Mosaico adaptado em duas colunas.",
    lastChange: "19/09/2026",
    canRestore: true,
  },
  {
    id: "vellora",
    name: "Chamada Vellora",
    position: 13,
    description: "Faixa institucional com a chamada comercial da Vellora Studio.",
    texts: [
      { label: "Selo", value: "Projeto demonstrativo" },
      { label: "Título", value: "Gostou desta experiência para o seu negócio?" },
      { label: "Botão 1", value: "Quero uma loja para minha marca" },
      { label: "Botão 2", value: "Conhecer a Vellora Studio" },
      { label: "Aviso", value: "Modelo demonstrativo criado pela Vellora Studio." },
    ],
    images: [],
    colors: [{ label: "Fundo da faixa", value: "secondary-100" }],
    links: [
      { label: "Quero uma loja para minha marca", href: "https://wa.me/message/6X77HRWZGKPQH1" },
      { label: "Conhecer a Vellora Studio", href: "https://vellorastudio.suapagina.net.br/" },
    ],
    desktop: "Cartão centralizado com dois botões.",
    mobile: "Botões empilhados.",
    lastChange: "19/09/2026",
    canRestore: true,
  },
  {
    id: "rodape",
    name: "Rodapé",
    position: 14,
    description: "Institucional, categorias, atendimento, aviso demonstrativo e créditos.",
    texts: [
      { label: "Assinatura", value: "Moda para viver cada versão de você." },
      { label: "Aviso", value: "Modelo demonstrativo criado pela Vellora Studio." },
      { label: "Créditos", value: "Desenvolvido pela Vellora Studio" },
    ],
    images: [],
    colors: [{ label: "Fundo", value: "primary-900" }],
    links: [
      { label: "Política de privacidade", href: "modal" },
      { label: "Desenvolvido pela Vellora Studio", href: "https://vellorastudio.suapagina.net.br/" },
    ],
    desktop: "Quatro colunas com aviso institucional.",
    mobile: "Colunas empilhadas.",
    lastChange: "19/09/2026",
    canRestore: true,
  },
];

/* ------------------------------------------------------------------ */
/* Registros demonstrativos (mascarados no modo público)               */
/* ------------------------------------------------------------------ */

export const demoCustomers: DemoCustomer[] = [
  {
    code: "AMB-CLI-00001",
    name: "Maria S.",
    phone: "(62) 9****-1234",
    email: "ma***@email.com",
    city: "Goiânia",
    state: "GO",
    orders: 3,
    totalSpent: 1189.7,
    lastOrderAt: "12/09/2026",
    status: "recorrente",
  },
  {
    code: "AMB-CLI-00002",
    name: "Juliana R.",
    phone: "(62) 9****-5678",
    email: "ju***@email.com",
    city: "Goiânia",
    state: "GO",
    orders: 1,
    totalSpent: 359.8,
    lastOrderAt: "08/09/2026",
    status: "novo",
  },
  {
    code: "AMB-CLI-00003",
    name: "Camila F.",
    phone: "(64) 9****-9012",
    email: "ca***@email.com",
    city: "Rio Verde",
    state: "GO",
    orders: 2,
    totalSpent: 728.6,
    lastOrderAt: "02/09/2026",
    status: "recorrente",
  },
  {
    code: "AMB-CLI-00004",
    name: "Beatriz M.",
    phone: "(11) 9****-3456",
    email: "be***@email.com",
    city: "São Paulo",
    state: "SP",
    orders: 1,
    totalSpent: 289.9,
    lastOrderAt: "21/08/2026",
    status: "inativo",
  },
];

export const demoOrders: DemoOrder[] = [
  {
    code: "AMB-PED-00003",
    date: "12/09/2026",
    time: "15:42",
    customerName: "Maria S.",
    customerPhone: "(62) 9****-1234",
    items: [
      { name: "Vestido Midi Serena", code: "AMB-VES-001", color: "Ameixa", size: "M", quantity: 1, unitPrice: 289.9 },
      { name: "Blusa Essencial Sofia", code: "AMB-BLU-001", color: "Rosa queimado", size: "G", quantity: 2, unitPrice: 119.9 },
    ],
    subtotal: 529.7,
    shipping: 0,
    discount: 0,
    total: 529.7,
    delivery: "Entrega local",
    address: "Rua das Flores, *** — Setor Bueno, Goiânia — GO",
    status: "Pedido iniciado — aguardando atendimento",
    paymentStatus: "A confirmar",
    deliveryStatus: "A combinar",
  },
  {
    code: "AMB-PED-00002",
    date: "08/09/2026",
    time: "10:18",
    customerName: "Juliana R.",
    customerPhone: "(62) 9****-5678",
    items: [
      { name: "Conjunto Elegance Valentina", code: "AMB-CON-005", color: "Terracota", size: "P", quantity: 1, unitPrice: 359.9 },
    ],
    subtotal: 359.9,
    shipping: 15,
    discount: 0,
    total: 374.9,
    delivery: "Entrega local",
    address: "Av. Central, *** — Centro, Goiânia — GO",
    status: "Disponibilidade confirmada",
    paymentStatus: "A confirmar",
    deliveryStatus: "A combinar",
  },
  {
    code: "AMB-PED-00001",
    date: "02/09/2026",
    time: "19:05",
    customerName: "Camila F.",
    customerPhone: "(64) 9****-9012",
    items: [
      { name: "Calça Alfaiataria Helena", code: "AMB-CAL-001", color: "Marrom café", size: "40", quantity: 1, unitPrice: 259.9 },
      { name: "Macacão Elegância", code: "AMB-MAC-001", color: "Ameixa", size: "M", quantity: 1, unitPrice: 279.9 },
    ],
    subtotal: 539.8,
    shipping: 24.9,
    discount: 0,
    total: 564.7,
    delivery: "Correios — PAC",
    address: "Rua *** — Setor Oeste, Rio Verde — GO · CEP *****-000",
    status: "Enviado",
    paymentStatus: "Pago",
    deliveryStatus: "Em trânsito",
  },
];

export const historyEntries: HistoryEntry[] = [
  {
    id: "h1",
    field: "Hero principal › Título",
    previous: "Moda feminina para a mulher brasileira",
    next: "Moda para viver cada versão de você",
    date: "19/09/2026",
    time: "14:32",
    author: "Vellora Studio",
  },
  {
    id: "h2",
    field: "Categoria Bermudas › Hero desktop",
    previous: "Imagem antiga (vertical reutilizada)",
    next: "Banner horizontal 1920 × 760 com bermuda visível",
    date: "19/09/2026",
    time: "14:10",
    author: "Vellora Studio",
  },
  {
    id: "h3",
    field: "Catálogo › AMB-BLU-004",
    previous: "Camisa Leve Cecília",
    next: "Blusa Leve Cecília",
    date: "18/09/2026",
    time: "17:48",
    author: "Vellora Studio",
  },
  {
    id: "h4",
    field: "Entregas › Frete grátis",
    previous: "Desativado",
    next: "Entrega local gratuita em Goiânia a partir de R$ 500,00",
    date: "18/09/2026",
    time: "11:20",
    author: "Vellora Studio",
  },
];

/* ------------------------------------------------------------------ */
/* Textos de apoio                                                      */
/* ------------------------------------------------------------------ */

export const seoConfig = {
  title: "Amora Belle — Moda feminina contemporânea",
  description:
    "Blusas, calças, vestidos, macacões, bermudas, conjuntos e saias para viver cada versão de você. Escolha suas peças e finalize pelo WhatsApp.",
  canonical: "https://amorabelle.exemplo.com/",
  ogImage:
    "https://readdy.ai/api/search-image?query=Editorial%20fashion%20photography%20of%20a%20confident%20Brazilian%20woman%20with%20natural%20curly%20hair%20wearing%20an%20elegant%20contemporary%20feminine%20outfit%20in%20deep%20plum%20and%20cream%20tones%2C%20standing%20in%20a%20warm%20sunlit%20urban%20Brazilian%20setting%20with%20soft%20neutral%20walls%2C%20natural%20daylight%2C%20realistic%20skin%20texture&width=1600&height=1000&seq=amora-hero-main-01&orientation=landscape",
  favicon: "favicon leaf (vinho)",
  robots: "index, follow",
};

export const storeSettings = {
  name: "Amora Belle",
  slogan: "Moda para viver cada versão de você.",
  phone: "(62) 99802-3960",
  whatsapp: "+55 62 99802-3960",
  instagram: "@vellora__studio",
  address: "Goiânia — GO · Atendimento digital em todo o Brasil",
  hours: "Segunda a sábado, das 9h às 19h (atendimento digital)",
  institutionalText:
    "A Amora Belle é um conceito de moda feminina que valoriza escolhas pessoais, conforto e confiança.",
  privacyPolicy: "Política de privacidade demonstrativa disponível no rodapé e no checkout.",
  exchangePolicy: "Troca orientada pela equipe pelo WhatsApp, conforme combinado no atendimento.",
};

export const catalogCollectedData = [
  "Nome completo",
  "WhatsApp",
  "E-mail (opcional)",
  "Cidade",
  "Estado",
  "Forma de recebimento",
  "Endereço (quando entrega)",
  "CEP (Correios e transportadora)",
  "Ponto de referência (opcional)",
  "Observações do pedido (opcional)",
  "Consentimento para uso dos dados",
];

export const panelModes = {
  viewNotice:
    "Modo de visualização — somente a administração Vellora pode realizar alterações.",
  adminNotice:
    "Modo administração Vellora — edições bloqueadas até a autenticação segura ser conectada.",
};